// src/pages/blog/BlogPost.js
import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Calendar,
  Clock,
  Eye,
  Tag as TagIcon,
  ChevronLeft,
  ChevronRight,
  Share2,
  Facebook,
  Twitter,
  Linkedin,
  AlertCircle,
  Bookmark,
  ArrowLeft,
  X,
  Check,
} from "lucide-react";
import { Helmet } from "react-helmet";
import { useConfig } from "../../context/ConfigContext";

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { config } = useConfig(); // Get config from context
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  // Copy URL function with visual feedback instead of alert
  const copyPageUrl = () => {
    try {
      navigator.clipboard.writeText(window.location.href);

      // Show "Copied!" status for 2 seconds
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy URL: ", err);
    }
  };

  useEffect(() => {
    try {
      // Set the document title using the site name from config
      document.title = "Blog | " + config?.siteName;
    } catch (err) {
      console.error("Error fetching page:", err);
    }
  }, [config?.siteName]); // Add config.siteName to the dependency array

  useEffect(() => {
    fetchPost();
    // Reset scroll position
    window.scrollTo(0, 0);
  }, [slug]);

  const fetchPost = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/blog/posts/${slug}`);

      if (!res.ok) {
        if (res.status === 404) {
          throw new Error("Post not found");
        }
        throw new Error("Failed to fetch post");
      }

      const post = await res.json();
      setPost(post);

      // Fetch related posts based on category
      if (post.category) {
        fetchRelatedPosts(post.category._id, post._id);
      }

      setError(null);
    } catch (err) {
      console.error("Error fetching post:", err);
      setError(err.message || "Failed to load the post");
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedPosts = async (categoryId, currentPostId) => {
    try {
      const res = await fetch(`/api/blog/posts?category=${categoryId}&limit=3`);

      if (!res.ok) throw new Error("Failed to fetch related posts");

      const data = await res.json();

      // Filter out the current post
      const filtered = data.posts.filter((post) => post._id !== currentPostId);
      setRelatedPosts(filtered.slice(0, 3));
    } catch (err) {
      console.error("Error fetching related posts:", err);
      // Don't set error state for related posts failure
    }
  };

  {
    /* Image Gallery Component */
  }
  const ImageGallery = ({ images, title }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);

    const openGallery = (index) => {
      setCurrentIndex(index);
      setIsOpen(true);
      // Prevent scrolling when gallery is open
      document.body.style.overflow = "hidden";
    };

    const closeGallery = () => {
      setIsOpen(false);
      // Re-enable scrolling
      document.body.style.overflow = "auto";
    };

    const goToPrevious = () => {
      setCurrentIndex((prevIndex) =>
        prevIndex === 0 ? images.length - 1 : prevIndex - 1
      );
    };

    const goToNext = () => {
      setCurrentIndex((prevIndex) =>
        prevIndex === images.length - 1 ? 0 : prevIndex + 1
      );
    };

    // Handle keyboard navigation
    React.useEffect(() => {
      const handleKeyDown = (e) => {
        if (!isOpen) return;

        if (e.key === "Escape") closeGallery();
        if (e.key === "ArrowLeft") goToPrevious();
        if (e.key === "ArrowRight") goToNext();
      };

      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isOpen]);

    return (
      <>
        {/* Thumbnail Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((image, index) => (
            <div
              key={index}
              className="group relative overflow-hidden rounded-lg shadow-md transition-all duration-300 hover:shadow-xl cursor-pointer"
              onClick={() => openGallery(index)}
            >
              <div className="aspect-w-1 aspect-h-1 w-full bg-gray-100">
                <img
                  src={image}
                  alt={`${title} - Image ${index + 1}`}
                  className="w-full h-full object-cover transform transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center">
                <div className="p-2 w-full text-white text-center">
                  <span className="text-sm font-medium">View Image</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Full Screen Gallery Modal */}
        {isOpen && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center">
            <div className="relative w-full h-full flex flex-col">
              {/* Controls */}
              <div className="absolute top-0 left-0 right-0 p-4 flex justify-between items-center z-10">
                <span className="text-white text-sm md:text-base">
                  {currentIndex + 1} / {images.length}
                </span>
                <button
                  onClick={closeGallery}
                  className="text-white p-2 rounded-full hover:bg-gray-800 focus:outline-none"
                  aria-label="Close gallery"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Main Image */}
              <div className="flex-grow flex items-center justify-center p-4 md:p-8">
                <img
                  src={images[currentIndex]}
                  alt={`${title} - Image ${currentIndex + 1}`}
                  className="max-h-full max-w-full object-contain"
                />
              </div>

              {/* Navigation Buttons */}
              <div className="absolute inset-y-0 left-0 flex items-center">
                <button
                  onClick={goToPrevious}
                  className="text-white p-2 m-2 rounded-full bg-black/30 hover:bg-black/50 focus:outline-none"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
              </div>
              <div className="absolute inset-y-0 right-0 flex items-center">
                <button
                  onClick={goToNext}
                  className="text-white p-2 m-2 rounded-full bg-black/30 hover:bg-black/50 focus:outline-none"
                  aria-label="Next image"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </div>

              {/* Caption */}
              <div className="p-4 text-center text-white">
                <p>
                  {title} - Image {currentIndex + 1}
                </p>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  // Format date
  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Handle share click
  const handleShare = () => {
    setShowShareOptions(!showShareOptions);
  };

  // Share on social media
  const shareOnSocial = (platform) => {
    const url = window.location.href;
    const title = post?.title || "Blog Post";

    let shareUrl;

    switch (platform) {
      case "facebook":
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          url
        )}`;
        break;
      case "twitter":
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(
          url
        )}&text=${encodeURIComponent(title)}`;
        break;
      case "linkedin":
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
          url
        )}`;
        break;
      default:
        return;
    }

    window.open(shareUrl, "_blank", "width=600,height=400");
  };

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse">
              <div className="h-96 bg-gray-200 rounded-lg mb-8"></div>
              <div className="h-12 bg-gray-200 rounded mb-4"></div>
              <div className="h-4 bg-gray-200 rounded mb-2 w-1/4"></div>
              <div className="flex gap-2 mb-6">
                <div className="h-6 w-16 bg-gray-200 rounded-full"></div>
                <div className="h-6 w-20 bg-gray-200 rounded-full"></div>
              </div>
              <div className="space-y-3">
                <div className="h-4 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              <p>{error || "Blog post not found"}</p>
              <button
                onClick={() => navigate("/blog")}
                className="font-bold hover:underline mt-2 inline-block"
                style={{ color: "#EF4444" }}
              >
                Return to Blog
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <Helmet>
        <title>{post.title + " |" + config.siteName}</title>
        <meta name="description" content={post.summary || post.description} />
      </Helmet>

      {/* Breadcrumbs */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <div className="text-sm breadcrumbs">
            <ul className="flex space-x-2">
              <li>
                <Link
                  to="/"
                  className="text-gray-500 hover:text-blue-600"
                  style={{ ":hover": { color: config.colors.primary } }}
                >
                  Home
                </Link>
                <span className="mx-2 text-gray-400">/</span>
              </li>
              <li>
                <Link
                  to="/blog"
                  className="text-gray-500 hover:text-blue-600"
                  style={{ ":hover": { color: config.colors.primary } }}
                >
                  Blog
                </Link>
                <span className="mx-2 text-gray-400">/</span>
              </li>
              <li className="text-gray-800 font-medium truncate">
                {post.title}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content */}
          <div className="lg:w-3/4">
            <article className="bg-white rounded-lg shadow-lg overflow-hidden">
              {/* Cover Image */}
              {post.coverImage && (
                <div className="h-64 md:h-96 relative">
                  <img
                    src={post.coverImage}
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              <div className="p-6 md:p-8">
                {/* Post Meta */}
                <div className="flex flex-wrap items-center mb-4 text-sm text-gray-500 gap-3">
                  <Link
                    to={`/blog/category/${post.category.slug}`}
                    className="px-3 py-1 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: `${config.colors.primary}20`,
                      color: `${config.colors.primary}E6`,
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = `${config.colors.primary}30`;
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = `${config.colors.primary}20`;
                    }}
                  >
                    {post.category.name}
                  </Link>

                  <span className="flex items-center">
                    <Calendar
                      className="h-4 w-4 mr-1"
                      style={{ color: `${config.colors.primary}AA` }}
                    />
                    {formatDate(post.publishedAt || post.createdAt)}
                  </span>
                </div>

                {/* Title */}
                <h1
                  className="text-3xl md:text-4xl font-bold mb-6"
                  style={{ color: config.colors.textPrimary || "#111827" }}
                >
                  {post.title}
                </h1>

                {/* Content */}
                <div className="post-content">
                  {/* Render the HTML content */}
                  <div
                    className="prose prose-lg max-w-none"
                    dangerouslySetInnerHTML={{ __html: post.content }}
                  />

                  {/* Display additional images */}
                  {post.images && post.images.length > 0 && (
                    <div className="mt-10 pt-6 border-t border-gray-100">
                      <h3
                        className="text-xl font-semibold mb-4 flex items-center"
                        style={{
                          color: config.colors.textPrimary || "#111827",
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 mr-2"
                          style={{ color: config.colors.primary }}
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Photo Gallery
                      </h3>

                      {/* Image Gallery with Modal */}
                      <ImageGallery images={post.images} title={post.title} />
                    </div>
                  )}

                  {/* Display videos */}
                  {post.videos && post.videos.length > 0 && (
                    <div className="mt-10 pt-6 border-t border-gray-100">
                      <h3
                        className="text-xl font-semibold mb-4 flex items-center"
                        style={{
                          color: config.colors.textPrimary || "#111827",
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 mr-2"
                          style={{ color: config.colors.primary }}
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                        </svg>
                        Video Content
                      </h3>
                      <div className="space-y-6 w-full">
                        {post.videos.map((video, index) => (
                          <div
                            key={index}
                            className="rounded-lg overflow-hidden shadow-md bg-gray-900 transition-shadow duration-300 hover:shadow-xl w-full"
                          >
                            <div className="aspect-w-16 aspect-h-9 w-full">
                              <video
                                src={video}
                                controls
                                poster={post.coverImage || ""}
                                className="w-full h-full object-cover"
                                preload="metadata"
                                controlsList="nodownload"
                              >
                                <p>
                                  Your browser doesn't support HTML5 video. Here
                                  is a <a href={video}>link to the video</a>{" "}
                                  instead.
                                </p>
                              </video>
                            </div>
                            <div className="p-3 bg-gray-800 text-gray-200">
                              <span className="text-sm font-medium">
                                {post.title} - Video {index + 1}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap items-center mt-6 mb-4 text-sm text-gray-500 gap-3">
                  <span className="flex items-center">
                    <Calendar
                      className="h-4 w-4 mr-1"
                      style={{ color: `${config.colors.primary}AA` }}
                    />
                    {formatDate(post.publishedAt || post.createdAt)}
                  </span>
                  <span className="flex items-center">
                    <Clock
                      className="h-4 w-4 mr-1"
                      style={{ color: `${config.colors.primary}AA` }}
                    />
                    {post.readTime} min read
                  </span>
                  <span className="flex items-center">
                    <Eye
                      className="h-4 w-4 mr-1"
                      style={{ color: `${config.colors.primary}AA` }}
                    />
                    {post.viewCount} views
                  </span>
                </div>

                {/* Tags */}
                {post.tags && post.tags.length > 0 && (
                  <div className="mt-8 pt-6 border-t">
                    <div className="flex flex-wrap items-center gap-2">
                      <TagIcon className="h-5 w-5 text-gray-400" />
                      {post.tags.map((tag) => (
                        <Link
                          key={tag._id}
                          to={`/blog/tag/${tag.slug}`}
                          className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm hover:bg-gray-200"
                        >
                          {tag.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Share */}
                <div className="mt-8 flex justify-between items-center">
                  <Link
                    to="/blog"
                    className="flex items-center"
                    style={{ color: config.colors.primary }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.color = `${config.colors.primary}DD`;
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.color = config.colors.primary;
                    }}
                  >
                    <ChevronLeft className="h-5 w-5 mr-1" />
                    Back to Blog
                  </Link>

                  <div className="relative">
                    <button
                      onClick={handleShare}
                      style={{
                        boxShadow: "none",
                        border: "none",
                        transform: "none",
                        color: config.colors.textSecondary || "#6B7280",
                      }}
                      className="flex items-center hover:text-gray-800"
                      onMouseOver={(e) => {
                        e.currentTarget.style.color =
                          config.colors.textPrimary || "#111827";
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.color =
                          config.colors.textSecondary || "#6B7280";
                      }}
                    >
                      <Share2 className="h-5 w-5 mr-1" />
                      Share
                    </button>

                    {showShareOptions && (
                      <div className="absolute right-0 mt-0 bg-white rounded-lg shadow-lg p-3 z-10">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => shareOnSocial("facebook")}
                            className="text-white p-2 rounded-full"
                            style={{ backgroundColor: "#1877F2" }}
                            aria-label="Share on Facebook"
                          >
                            <Facebook className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => shareOnSocial("twitter")}
                            className="text-white p-2 rounded-full"
                            style={{ backgroundColor: "#1DA1F2" }}
                            aria-label="Share on Twitter"
                          >
                            <Twitter className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => shareOnSocial("linkedin")}
                            className="text-white p-2 rounded-full"
                            style={{ backgroundColor: "#0A66C2" }}
                            aria-label="Share on LinkedIn"
                          >
                            <Linkedin className="h-5 w-5" />
                          </button>
                          <div className="flex items-center">
                            <button
                              onClick={copyPageUrl}
                              className={`flex items-center justify-center transition-colors duration-200 px-2 text-sm rounded-r-md p-1 ${
                                isCopied
                                  ? "text-white"
                                  : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                              }`}
                              style={
                                isCopied ? { backgroundColor: "#10B981" } : {}
                              }
                            >
                              {isCopied ? (
                                <>
                                  <Check className="h-3 w-3 mr-1" />
                                  Copied
                                </>
                              ) : (
                                "Copy"
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="py-4"></div>
              </div>
            </article>

            {/* Related Posts */}
            {relatedPosts.length > 0 && (
              <div className="mt-12">
                <h2
                  className="text-2xl font-bold mb-6"
                  style={{ color: config.colors.textPrimary || "#111827" }}
                >
                  Related Articles
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {relatedPosts.map((post) => (
                    <div
                      key={post._id}
                      className="bg-white rounded-lg shadow overflow-hidden hover:shadow-md transition-shadow"
                    >
                      <Link to={`/blog/${post.slug}`}>
                        <div className="h-40 bg-gray-200">
                          {post.coverImage ? (
                            <img
                              src={post.coverImage}
                              alt={post.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <span className="text-gray-400">No image</span>
                            </div>
                          )}
                        </div>
                      </Link>
                      <div className="p-4">
                        <h3 className="font-bold mb-2 line-clamp-2">
                          <Link
                            to={`/blog/${post.slug}`}
                            style={{
                              color: config.colors.textPrimary || "#111827",
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.color =
                                config.colors.primary;
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.color =
                                config.colors.textPrimary || "#111827";
                            }}
                          >
                            {post.title}
                          </Link>
                        </h3>
                        <div className="text-gray-500 text-sm mb-2 flex items-center">
                          <Calendar
                            className="h-4 w-4 mr-1"
                            style={{ color: `${config.colors.primary}AA` }}
                          />
                          {formatDate(post.publishedAt || post.createdAt)}
                        </div>
                        <p className="text-gray-600 text-sm line-clamp-2">
                          {post.excerpt}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:w-1/4">
            {/* Author Info */}
            <div className="bg-white rounded-lg shadow p-6 mb-8">
              <h3
                className="text-xl font-bold mb-4"
                style={{ color: config.colors.textPrimary || "#111827" }}
              >
                Author
              </h3>
              <div className="flex items-center">
                <div className="bg-gray-200 rounded-full h-12 w-12 flex items-center justify-center text-gray-600 font-bold text-xl mr-4">
                  {post.author?.firstName?.charAt(0) || "A"}
                </div>
                <div>
                  <p
                    className="font-medium"
                    style={{ color: config.colors.textPrimary || "#111827" }}
                  >
                    {/* {post.author?.firstName} {post.author?.lastName} */}
                    BagBox Team
                  </p>
                </div>
              </div>
            </div>

            {/* Categories */}
            <div className="bg-white rounded-lg shadow p-6 mb-8">
              <h3
                className="text-xl font-bold mb-4"
                style={{ color: config.colors.textPrimary || "#111827" }}
              >
                Categories
              </h3>
              <ul>
                <li>
                  <Link
                    to={`/blog/category/${post.category.slug}`}
                    className="flex items-center justify-between py-2 px-3 rounded hover:bg-gray-50"
                    style={{
                      ":hover": {
                        backgroundColor: `${config.colors.primary}10`,
                      },
                    }}
                  >
                    <span
                      style={{ color: config.colors.textPrimary || "#111827" }}
                    >
                      {post.category.name}
                    </span>
                    <Bookmark className="h-4 w-4 text-gray-400" />
                  </Link>
                </li>
              </ul>
            </div>

            {/* Call to Action */}
            <div
              className="rounded-lg shadow p-6"
              style={{ backgroundColor: `${config.colors.primary}10` }}
            >
              <h3
                className="text-xl font-bold mb-4"
                style={{ color: config.colors.textPrimary || "#111827" }}
              >
                Need Help?
              </h3>
              <p
                className="mb-4"
                style={{ color: config.colors.textSecondary || "#6B7280" }}
              >
                Looking for high quality solutions for your business? Contact
                our team today to get a quote or discuss your needs.
              </p>
              <Link
                to="/contactUs"
                className="block text-white px-4 py-2 rounded text-center"
                style={{
                  backgroundColor: config.colors.primary,
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = `${config.colors.primary}DD`;
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = config.colors.primary;
                }}
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* CSS for spinner and line clamp */}
      <style jsx>{`
        .spinner {
          border: 3px solid rgba(0, 0, 0, 0.1);
          border-radius: 50%;
          border-top: 3px solid ${config.colors.primary || "#4f46e5"};
          width: 24px;
          height: 24px;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .prose img {
          max-width: 100%;
          height: auto;
          border-radius: 0.5rem;
        }

        .prose a {
          color: ${config.colors.primary || "#2563eb"};
          text-decoration: underline;
        }

        .prose h2 {
          font-size: 1.5rem;
          font-weight: 700;
          margin-top: 2rem;
          margin-bottom: 1rem;
          color: ${config.colors.textPrimary || "#111827"};
        }

        .prose h3 {
          font-size: 1.25rem;
          font-weight: 700;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
          color: ${config.colors.textPrimary || "#111827"};
        }

        .prose blockquote {
          border-left: 4px solid #e5e7eb;
          padding-left: 1rem;
          font-style: italic;
          color: ${config.colors.textSecondary || "#6B7280"};
        }

        .prose ul {
          list-style-type: disc;
          padding-left: 1.5rem;
          margin-top: 1rem;
          margin-bottom: 1rem;
        }

        .prose ol {
          list-style-type: decimal;
          padding-left: 1.5rem;
          margin-top: 1rem;
          margin-bottom: 1rem;
        }

        .prose p {
          margin-top: 1rem;
          margin-bottom: 1rem;
          color: ${config.colors.textSecondary || "#6B7280"};
        }

        .prose {
          max-width: 100%;
          line-height: 1.75;
        }

        .prose p {
          margin-top: 1.25em;
          margin-bottom: 1.25em;
        }

        .prose h2 {
          margin-top: 2em;
          margin-bottom: 1em;
          font-size: 1.5em;
          font-weight: 700;
          line-height: 1.3;
        }

        .prose h3 {
          margin-top: 1.6em;
          margin-bottom: 0.8em;
          font-size: 1.3em;
          font-weight: 600;
        }

        .prose blockquote {
          margin: 1.5em 0;
          padding: 0.5em 1em;
          border-left: 4px solid ${config.colors.textSecondary || "#4B5563"};
          font-style: italic;
          background-color: #f9fafb;
        }

        .post-header {
          padding-bottom: 2rem;
          border-bottom: 1px solid #e5e7eb;
          margin-bottom: 2rem;
        }

        .post-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .post-content img {
          border-radius: 0.5rem;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
            0 2px 4px -1px rgba(0, 0, 0, 0.06);
        }
      `}</style>
    </div>
  );
};

export default BlogPost;

// // before coloring from configcontext
// // src/pages/blog/BlogPost.js
// import React, { useState, useEffect } from 'react';
// import { useParams, Link, useNavigate } from 'react-router-dom';
// import {
//   Calendar, Clock, Eye, Tag as TagIcon, ChevronLeft, ChevronRight, Share2, Facebook,
//   Twitter, Linkedin, AlertCircle, Bookmark, ArrowLeft, X, Check,
// } from 'lucide-react';
// import { Helmet } from 'react-helmet';

// const BlogPost = () => {
//   const { slug } = useParams();
//   const navigate = useNavigate();
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [post, setPost] = useState(null);
//   const [relatedPosts, setRelatedPosts] = useState([]);
//   const [showShareOptions, setShowShareOptions] = useState(false);
//   const [isCopied, setIsCopied] = useState(false);

//   // Copy URL function with visual feedback instead of alert
//   const copyPageUrl = () => {
//     try {
//       navigator.clipboard.writeText(window.location.href);

//       // Show "Copied!" status for 2 seconds
//       setIsCopied(true);
//       setTimeout(() => {
//         setIsCopied(false);
//       }, 2000);

//       // Alternative approach with a temporary element if clipboard API isn't available
//       /*
//       const el = document.createElement('textarea');
//       el.value = window.location.href;
//       document.body.appendChild(el);
//       el.select();
//       document.execCommand('copy');
//       document.body.removeChild(el);
//       setIsCopied(true);
//       setTimeout(() => {
//         setIsCopied(false);
//       }, 2000);
//       */
//     } catch (err) {
//       console.error('Failed to copy URL: ', err);
//     }
//   };

//   useEffect(() => {
//     fetchPost();
//     // Reset scroll position
//     window.scrollTo(0, 0);
//   }, [slug]);

//   const fetchPost = async () => {
//     setLoading(true);
//     try {
//       const res = await fetch(`/api/blog/posts/${slug}`);

//       if (!res.ok) {
//         if (res.status === 404) {
//           throw new Error('Post not found');
//         }
//         throw new Error('Failed to fetch post');
//       }

//       const post = await res.json();
//       setPost(post);

//       // Fetch related posts based on category
//       if (post.category) {
//         fetchRelatedPosts(post.category._id, post._id);
//       }

//       setError(null);
//     } catch (err) {
//       console.error('Error fetching post:', err);
//       setError(err.message || 'Failed to load the post');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const fetchRelatedPosts = async (categoryId, currentPostId) => {
//     try {
//       const res = await fetch(`/api/blog/posts?category=${categoryId}&limit=3`);

//       if (!res.ok) throw new Error('Failed to fetch related posts');

//       const data = await res.json();

//       // Filter out the current post
//       const filtered = data.posts.filter(post => post._id !== currentPostId);
//       setRelatedPosts(filtered.slice(0, 3));
//     } catch (err) {
//       console.error('Error fetching related posts:', err);
//       // Don't set error state for related posts failure
//     }
//   };

//   {/* Image Gallery Component */}
//   const ImageGallery = ({ images, title }) => {
//     const [isOpen, setIsOpen] = useState(false);
//     const [currentIndex, setCurrentIndex] = useState(0);

//     const openGallery = (index) => {
//       setCurrentIndex(index);
//       setIsOpen(true);
//       // Prevent scrolling when gallery is open
//       document.body.style.overflow = 'hidden';
//     };

//     const closeGallery = () => {
//       setIsOpen(false);
//       // Re-enable scrolling
//       document.body.style.overflow = 'auto';
//     };

//     const goToPrevious = () => {
//       setCurrentIndex((prevIndex) => (prevIndex === 0 ? images.length - 1 : prevIndex - 1));
//     };

//     const goToNext = () => {
//       setCurrentIndex((prevIndex) => (prevIndex === images.length - 1 ? 0 : prevIndex + 1));
//     };

//     // Handle keyboard navigation
//     React.useEffect(() => {
//       const handleKeyDown = (e) => {
//         if (!isOpen) return;

//         if (e.key === 'Escape') closeGallery();
//         if (e.key === 'ArrowLeft') goToPrevious();
//         if (e.key === 'ArrowRight') goToNext();
//       };

//       window.addEventListener('keydown', handleKeyDown);
//       return () => window.removeEventListener('keydown', handleKeyDown);
//     }, [isOpen]);

//     return (
//       <>
//         {/* Thumbnail Grid */}
//         <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
//           {images.map((image, index) => (
//             <div
//               key={index}
//               className="group relative overflow-hidden rounded-lg shadow-md transition-all duration-300 hover:shadow-xl cursor-pointer"
//               onClick={() => openGallery(index)}
//             >
//               <div className="aspect-w-1 aspect-h-1 w-full bg-gray-100">
//                 <img
//                   src={image}
//                   alt={`${title} - Image ${index + 1}`}
//                   className="w-full h-full object-cover transform transition-transform duration-300 group-hover:scale-105"
//                   loading="lazy"
//                 />
//               </div>
//               <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center">
//                 <div className="p-2 w-full text-white text-center">
//                   <span className="text-sm font-medium">View Image</span>
//                 </div>
//               </div>
//             </div>
//           ))}
//         </div>

//         {/* Full Screen Gallery Modal */}
//         {isOpen && (
//           <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center">
//             <div className="relative w-full h-full flex flex-col">
//               {/* Controls */}
//               <div className="absolute top-0 left-0 right-0 p-4 flex justify-between items-center z-10">
//                 <span className="text-white text-sm md:text-base">
//                   {currentIndex + 1} / {images.length}
//                 </span>
//                 <button
//                   onClick={closeGallery}
//                   className="text-white p-2 rounded-full hover:bg-gray-800 focus:outline-none"
//                   aria-label="Close gallery"
//                 >
//                   <X className="w-6 h-6" />
//                 </button>
//               </div>

//               {/* Main Image */}
//               <div className="flex-grow flex items-center justify-center p-4 md:p-8">
//                 <img
//                   src={images[currentIndex]}
//                   alt={`${title} - Image ${currentIndex + 1}`}
//                   className="max-h-full max-w-full object-contain"
//                 />
//               </div>

//               {/* Navigation Buttons */}
//               <div className="absolute inset-y-0 left-0 flex items-center">
//                 <button
//                   onClick={goToPrevious}
//                   className="text-white p-2 m-2 rounded-full bg-black/30 hover:bg-black/50 focus:outline-none"
//                   aria-label="Previous image"
//                 >
//                   <ChevronLeft className="w-6 h-6" />
//                 </button>
//               </div>
//               <div className="absolute inset-y-0 right-0 flex items-center">
//                 <button
//                   onClick={goToNext}
//                   className="text-white p-2 m-2 rounded-full bg-black/30 hover:bg-black/50 focus:outline-none"
//                   aria-label="Next image"
//                 >
//                   <ChevronRight className="w-6 h-6" />
//                 </button>
//               </div>

//               {/* Caption */}
//               <div className="p-4 text-center text-white">
//                 <p>{title} - Image {currentIndex + 1}</p>
//               </div>
//             </div>
//           </div>
//         )}
//       </>
//     );
//   };

//   // Format date
//   const formatDate = (dateString) => {
//     const options = { year: 'numeric', month: 'long', day: 'numeric' };
//     return new Date(dateString).toLocaleDateString(undefined, options);
//   };

//   // Handle share click
//   const handleShare = () => {
//     setShowShareOptions(!showShareOptions);
//   };

//   // Share on social media
//   const shareOnSocial = (platform) => {
//     const url = window.location.href;
//     const title = post?.title || 'Blog Post';

//     let shareUrl;

//     switch (platform) {
//       case 'facebook':
//         shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
//         break;
//       case 'twitter':
//         shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;
//         break;
//       case 'linkedin':
//         shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
//         break;
//       default:
//         return;
//     }

//     window.open(shareUrl, '_blank', 'width=600,height=400');
//   };

//   if (loading) {
//     return (
//       <div className="bg-gray-50 min-h-screen">
//         <div className="container mx-auto px-4 py-12">
//           <div className="max-w-4xl mx-auto">
//             <div className="animate-pulse">
//               <div className="h-96 bg-gray-200 rounded-lg mb-8"></div>
//               <div className="h-12 bg-gray-200 rounded mb-4"></div>
//               <div className="h-4 bg-gray-200 rounded mb-2 w-1/4"></div>
//               <div className="flex gap-2 mb-6">
//                 <div className="h-6 w-16 bg-gray-200 rounded-full"></div>
//                 <div className="h-6 w-20 bg-gray-200 rounded-full"></div>
//               </div>
//               <div className="space-y-3">
//                 <div className="h-4 bg-gray-200 rounded"></div>
//                 <div className="h-4 bg-gray-200 rounded"></div>
//                 <div className="h-4 bg-gray-200 rounded"></div>
//                 <div className="h-4 bg-gray-200 rounded w-5/6"></div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   if (error || !post) {
//     return (
//       <div className="bg-gray-50 min-h-screen">
//         <div className="container mx-auto px-4 py-8">
//           <div className="max-w-4xl mx-auto">
//             <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
//               <p>{error || 'Blog post not found'}</p>
//               <button
//                 onClick={() => navigate('/blog')}
//                 className="text-red-700 font-bold hover:underline mt-2 inline-block"
//               >
//                 Return to Blog
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="bg-gray-50 min-h-screen">
//       <Helmet>
//         <title>{post.title} | BagBox</title>
//         <meta name="description" content={post.summary || post.description} />
//       </Helmet>

//       {/* Breadcrumbs */}
//       <div className="bg-white border-b border-gray-200">
//         <div className="container mx-auto px-4 py-3">
//           <div className="text-sm breadcrumbs">
//             <ul className="flex space-x-2">
//               <li>
//                 <Link to="/" className="text-gray-500 hover:text-blue-600">Home</Link>
//                 <span className="mx-2 text-gray-400">/</span>
//               </li>
//               <li>
//                 <Link to="/blog" className="text-gray-500 hover:text-blue-600">Blog</Link>
//                 <span className="mx-2 text-gray-400">/</span>
//               </li>
//               <li className="text-gray-800 font-medium truncate">{post.title}</li>
//             </ul>
//           </div>
//         </div>
//       </div>

//       <div className="container mx-auto px-4 py-8 max-w-7xl mx-auto">
//         {/* Breadcrumb */}
//         {/* <div className="mb-6">
//           <nav className="flex items-center text-sm text-gray-500">
//             <Link to="/" className="hover:text-blue-600">Home</Link>
//             <span className="mx-2">&#8250;</span>
//             <Link to="/blog" className="hover:text-blue-600">Blog</Link>
//             <span className="mx-2">&#8250;</span>
//             <Link
//               to={`/blog/category/${post.category.slug}`}
//               className="hover:text-blue-600"
//             >
//               {post.category.name}
//             </Link>
//             <span className="mx-2">&#8250;</span>
//             <span className="text-gray-700">{post.title}</span>
//           </nav>
//         </div> */}

//         <div className="flex flex-col lg:flex-row gap-8">
//           {/* Main Content */}
//           <div className="lg:w-3/4">
//             <article className="bg-white rounded-lg shadow-lg overflow-hidden">
//               {/* Cover Image */}
//               {post.coverImage && (
//                 <div className="h-64 md:h-96 relative">
//                   <img
//                     src={post.coverImage}
//                     alt={post.title}
//                     className="w-full h-full object-cover"
//                   />
//                 </div>
//               )}

//               <div className="p-6 md:p-8">
//                 {/* Post Meta */}
//                 <div className="flex flex-wrap items-center mb-4 text-sm text-gray-500 gap-3">
//                   <Link
//                     to={`/blog/category/${post.category.slug}`}
//                     className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-medium hover:bg-blue-200"
//                   >
//                     {post.category.name}
//                   </Link>

//                   <span className="flex items-center">
//                     <Calendar className="h-4 w-4 mr-1" />
//                     {formatDate(post.publishedAt || post.createdAt)}
//                   </span>

//                   {/* <span className="flex items-center">
//                     <Clock className="h-4 w-4 mr-1" />
//                     {post.readTime} min read
//                   </span>

//                   <span className="flex items-center">
//                     <Eye className="h-4 w-4 mr-1" />
//                     {post.viewCount} views
//                   </span> */}
//                 </div>

//                 {/* Title */}
//                 <h1 className="text-3xl md:text-4xl font-bold mb-6">{post.title}</h1>

//                 {/* Content */}
//                 <div className="post-content">
//                   {/* Render the HTML content */}
//                   <div
//                     className="prose prose-lg max-w-none"
//                     dangerouslySetInnerHTML={{ __html: post.content }}
//                   />

//                   {/* Display additional images */}
//                   {/* {post.images && post.images.length > 0 && (
//                     <div className="mt-8">
//                       <h3 className="text-xl font-semibold mb-4">Additional Images</h3>
//                       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
//                         {post.images.map((image, index) => (
//                           <div key={index} className="rounded-lg overflow-hidden shadow-md">
//                             <img
//                               src={image}
//                               alt={`Additional image ${index + 1}`}
//                               className="w-full h-auto"
//                             />
//                           </div>
//                         ))}
//                       </div>
//                     </div>
//                   )} */}

//                   {/* Display videos */}
//                   {/* {post.videos && post.videos.length > 0 && (
//                     <div className="mt-8">
//                       <h3 className="text-xl font-semibold mb-4">Videos</h3>
//                       <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                         {post.videos.map((video, index) => (
//                           <div key={index} className="rounded-lg overflow-hidden shadow-md">
//                             <video
//                               src={video}
//                               controls
//                               className="w-full h-auto"
//                               preload="metadata"
//                             />
//                           </div>
//                         ))}
//                       </div>
//                     </div>
//                   )} */}
//                   {/* Display additional images */}
//                   {post.images && post.images.length > 0 && (
//                     <div className="mt-10 pt-6 border-t border-gray-100">
//                       <h3 className="text-xl font-semibold mb-4 flex items-center">
//                         <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
//                           <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
//                         </svg>
//                         Photo Gallery
//                       </h3>

//                       {/* Image Gallery with Modal */}
//                       <ImageGallery images={post.images} title={post.title} />
//                     </div>
//                   )}

//                   {/* Display videos */}
//                   {post.videos && post.videos.length > 0 && (
//                     <div className="mt-10 pt-6 border-t border-gray-100">
//                       <h3 className="text-xl font-semibold mb-4 flex items-center">
//                         <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
//                           <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
//                         </svg>
//                         Video Content
//                       </h3>
//                       <div className="space-y-6 w-full">
//                         {post.videos.map((video, index) => (
//                           <div key={index} className="rounded-lg overflow-hidden shadow-md bg-gray-900 transition-shadow duration-300 hover:shadow-xl w-full">
//                             <div className="aspect-w-16 aspect-h-9 w-full">
//                               <video
//                                 src={video}
//                                 controls
//                                 poster={post.coverImage || ''}
//                                 className="w-full h-full object-cover"
//                                 preload="metadata"
//                                 controlsList="nodownload"
//                               >
//                                 <p>Your browser doesn't support HTML5 video. Here is a <a href={video}>link to the video</a> instead.</p>
//                               </video>
//                             </div>
//                             <div className="p-3 bg-gray-800 text-gray-200">
//                               <span className="text-sm font-medium">{post.title} - Video {index + 1}</span>
//                             </div>
//                           </div>
//                         ))}
//                       </div>
//                     </div>
//                   )}
//                 </div>

//                 <div className="flex flex-wrap items-center mt-6 mb-4 text-sm text-gray-500 gap-3">
//                   <span className="flex items-center">
//                     <Calendar className="h-4 w-4 mr-1" />
//                     {formatDate(post.publishedAt || post.createdAt)}
//                   </span>
//                   <span className="flex items-center">
//                     <Clock className="h-4 w-4 mr-1" />
//                     {post.readTime} min read
//                   </span>
//                   <span className="flex items-center">
//                     <Eye className="h-4 w-4 mr-1" />
//                     {post.viewCount} views
//                   </span>
//                 </div>

//                 {/* Tags */}
//                 {post.tags && post.tags.length > 0 && (
//                   <div className="mt-8 pt-6 border-t">
//                     <div className="flex flex-wrap items-center gap-2">
//                       <TagIcon className="h-5 w-5 text-gray-400" />
//                       {post.tags.map(tag => (
//                         <Link
//                           key={tag._id}
//                           to={`/blog/tag/${tag.slug}`}
//                           className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm hover:bg-gray-200"
//                         >
//                           {tag.name}
//                         </Link>
//                       ))}
//                     </div>
//                   </div>
//                 )}

//                 {/* Share */}
//                 <div className="mt-8 flex justify-between items-center">
//                   <Link
//                     to="/blog"
//                     className="flex items-center text-blue-600 hover:text-blue-800"
//                   >
//                     <ChevronLeft className="h-5 w-5 mr-1" />
//                     Back to Blog
//                   </Link>

//                   <div className="relative">
//                     <button
//                       onClick={handleShare}
//                       style={{
//                         boxShadow: 'none',
//                         border: 'none',
//                         transform: 'none',
//                       }}
//                       className="flex items-center text-gray-600 hover:text-gray-800"
//                     >
//                       <Share2 className="h-5 w-5 mr-1" />
//                       Share
//                     </button>

//                     {showShareOptions && (
//                       <div className="absolute right-0 mt-0 bg-white rounded-lg shadow-lg p-3 z-10">
//                         <div className="flex space-x-2">
//                           <button
//                             onClick={() => shareOnSocial('facebook')}
//                             className="bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700"
//                             aria-label="Share on Facebook"
//                           >
//                             <Facebook className="h-5 w-5" />
//                           </button>
//                           <button
//                             onClick={() => shareOnSocial('twitter')}
//                             className="bg-blue-400 text-white p-2 rounded-full hover:bg-blue-500"
//                             aria-label="Share on Twitter"
//                           >
//                             <Twitter className="h-5 w-5" />
//                           </button>
//                           <button
//                             onClick={() => shareOnSocial('linkedin')}
//                             className="bg-blue-700 text-white p-2 rounded-full hover:bg-blue-800"
//                             aria-label="Share on LinkedIn"
//                           >
//                             <Linkedin className="h-5 w-5" />
//                           </button>
//                           <div className="flex items-center">
//                             {/* <input
//                               type="text"
//                               readOnly
//                               value={window.location.href}
//                               className="text-sm border rounded-l-md p-1 w-full focus:outline-none focus:ring-1 focus:ring-blue-500"
//                             /> */}
//                             <button
//                               onClick={copyPageUrl}
//                               className={`flex items-center justify-center transition-colors duration-200 px-2 text-sm rounded-r-md p-1 ${
//                                 isCopied
//                                   ? 'bg-green-500 text-white'
//                                   : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
//                               }`}
//                             >
//                               {isCopied ? (
//                                 <>
//                                   <Check className="h-3 w-3 mr-1" />
//                                   Copied
//                                 </>
//                               ) : (
//                                 'Copy'
//                               )}
//                             </button>
//                           </div>
//                         </div>
//                       </div>
//                     )}
//                   </div>
//                 </div>
//                <div className='py-4'></div>
//               </div>
//             </article>

//             {/* Related Posts */}
//             {relatedPosts.length > 0 && (
//               <div className="mt-12">
//                 <h2 className="text-2xl font-bold mb-6">Related Articles</h2>
//                 <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
//                   {relatedPosts.map(post => (
//                     <div key={post._id} className="bg-white rounded-lg shadow overflow-hidden hover:shadow-md transition-shadow">
//                       <Link to={`/blog/${post.slug}`}>
//                         <div className="h-40 bg-gray-200">
//                           {post.coverImage ? (
//                             <img
//                               src={post.coverImage}
//                               alt={post.title}
//                               className="w-full h-full object-cover"
//                             />
//                           ) : (
//                             <div className="w-full h-full flex items-center justify-center">
//                               <span className="text-gray-400">No image</span>
//                             </div>
//                           )}
//                         </div>
//                       </Link>
//                       <div className="p-4">
//                       <h3 className="font-bold hover:text-blue-600 mb-2 line-clamp-2">
//                           <Link to={`/blog/${post.slug}`}>
//                             {post.title}
//                           </Link>
//                         </h3>
//                         <div className="text-gray-500 text-sm mb-2 flex items-center">
//                           <Calendar className="h-4 w-4 mr-1" />
//                           {formatDate(post.publishedAt || post.createdAt)}
//                         </div>
//                         <p className="text-gray-600 text-sm line-clamp-2">
//                           {post.excerpt}
//                         </p>
//                       </div>
//                     </div>
//                   ))}
//                 </div>
//               </div>
//             )}
//           </div>

//           {/* Sidebar */}
//           <div className="lg:w-1/4">
//             {/* Author Info */}
//             <div className="bg-white rounded-lg shadow p-6 mb-8">
//               <h3 className="text-xl font-bold mb-4">Author</h3>
//               <div className="flex items-center">
//                 <div className="bg-gray-200 rounded-full h-12 w-12 flex items-center justify-center text-gray-600 font-bold text-xl mr-4">
//                   {post.author?.firstName?.charAt(0) || 'A'}
//                 </div>
//                 <div>
//                   <p className="font-medium">
//                     {/* {post.author?.firstName} {post.author?.lastName} */}
//                     BagBox Team
//                   </p>
//                   {/* <p className="text-gray-500 text-sm">
//                     {post.author?.email || 'Admin'}
//                   </p> */}
//                 </div>
//               </div>
//             </div>

//             {/* Categories */}
//             <div className="bg-white rounded-lg shadow p-6 mb-8">
//               <h3 className="text-xl font-bold mb-4">Categories</h3>
//               <ul>
//                 <li>
//                   <Link
//                     to={`/blog/category/${post.category.slug}`}
//                     className="flex items-center justify-between py-2 px-3 rounded hover:bg-gray-50"
//                   >
//                     <span>{post.category.name}</span>
//                     <Bookmark className="h-4 w-4 text-gray-400" />
//                   </Link>
//                 </li>
//               </ul>
//             </div>

//             {/* Call to Action */}
//             <div className="bg-blue-50 rounded-lg shadow p-6">
//               <h3 className="text-xl font-bold mb-4">Need Help?</h3>
//               <p className="text-gray-600 mb-4">
//                 Looking for premium packaging solutions for your business? Contact our team today to get a quote or discuss your needs.
//               </p>
//               <Link
//                 to="/contactUs"
//                 className="bg-blue-600 text-white px-4 py-2 rounded block text-center hover:bg-blue-700"
//               >
//                 Contact Us
//               </Link>
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* CSS for spinner and line clamp */}
//       <style jsx>{`
//         .spinner {
//           border: 3px solid rgba(0, 0, 0, 0.1);
//           border-radius: 50%;
//           border-top: 3px solid #4f46e5;
//           width: 24px;
//           height: 24px;
//           animation: spin 1s linear infinite;
//         }

//         @keyframes spin {
//           0% { transform: rotate(0deg); }
//           100% { transform: rotate(360deg); }
//         }

//         .line-clamp-2 {
//           display: -webkit-box;
//           -webkit-line-clamp: 2;
//           -webkit-box-orient: vertical;
//           overflow: hidden;
//         }

//         .prose img {
//           max-width: 100%;
//           height: auto;
//           border-radius: 0.5rem;
//         }

//         .prose a {
//           color: #2563eb;
//           text-decoration: underline;
//         }

//         .prose h2 {
//           font-size: 1.5rem;
//           font-weight: 700;
//           margin-top: 2rem;
//           margin-bottom: 1rem;
//         }

//         .prose h3 {
//           font-size: 1.25rem;
//           font-weight: 700;
//           margin-top: 1.5rem;
//           margin-bottom: 0.75rem;
//         }

//         .prose blockquote {
//           border-left: 4px solid #e5e7eb;
//           padding-left: 1rem;
//           font-style: italic;
//           color: #4b5563;
//         }

//         .prose ul {
//           list-style-type: disc;
//           padding-left: 1.5rem;
//           margin-top: 1rem;
//           margin-bottom: 1rem;
//         }

//         .prose ol {
//           list-style-type: decimal;
//           padding-left: 1.5rem;
//           margin-top: 1rem;
//           margin-bottom: 1rem;
//         }

//         .prose p {
//           margin-top: 1rem;
//           margin-bottom: 1rem;
//         }

//         .prose {
//           max-width: 100%;
//           line-height: 1.75;
//         }

//         .prose p {
//           margin-top: 1.25em;
//           margin-bottom: 1.25em;
//         }

//         .prose h2 {
//           margin-top: 2em;
//           margin-bottom: 1em;
//           font-size: 1.5em;
//           font-weight: 700;
//           line-height: 1.3;
//           color: #111827;
//         }

//         .prose h3 {
//           margin-top: 1.6em;
//           margin-bottom: 0.8em;
//           font-size: 1.3em;
//           font-weight: 600;
//           color: #111827;
//         }

//         .prose blockquote {
//           margin: 1.5em 0;
//           padding: 0.5em 1em;
//           border-left: 4px solid #4B5563;
//           font-style: italic;
//           color: #4B5563;
//           background-color: #F9FAFB;
//         }

//         .post-header {
//           padding-bottom: 2rem;
//           border-bottom: 1px solid #e5e7eb;
//           margin-bottom: 2rem;
//         }

//         .post-meta {
//           display: flex;
//           flex-wrap: wrap;
//           gap: 1rem;
//           margin-bottom: 1rem;
//         }

//         .post-content img {
//           border-radius: 0.5rem;
//           box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
//         }
//       `}</style>
//     </div>
//   );
// };

// export default BlogPost;
